<script setup lang="ts">
import { computed, nextTick, onMounted, watch, onUnmounted } from 'vue';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { AppFirstLevelPanel } from '@libs/ui';

import { DynamicFullScreenMap, MapType } from '../maps';
import { useMapStore } from '../../core/stores/mapStore/mapStore';

import { ActivityDetailsTemplateId } from './models/activityDetailsTemplateId';
import type { ActivityDetailsData, SelectTicketEventPayload } from './models';
import ActivityDetailsFooter from './components/ActivityDetailsFooter.vue';
import ActivityDetailsGallery from './components/ActivityDetailsGallery.vue';
import ActivityDetailsDescription from './components/description/ActivityDetailsDescription.vue';
import ActivityDetailsTickets from './components/ticket/ActivityDetailsTickets.vue';
import { useActivityDetailsMapStore } from './stores/activityDetailsMapStore';
import { useActivityDetailsTicketsStore } from './stores/activityDetailsTicketsStore';
import { useActivityDetailsStore } from './stores/activityDetailsStore';
import ActivityDetailsTicketsLoader from './components/loaders/ActivityDetailsTicketsLoader.vue';
import ActivityDetailsGalleryLoader from './components/loaders/ActivityDetailsGalleryLoader.vue';
import ActivityDetailsFooterLoader from './components/loaders/ActivityDetailsFooterLoader.vue';

interface ActivityDetailsPanelProps {
  activityDetails: ActivityDetailsData;
}

const props = defineProps<ActivityDetailsPanelProps>();

interface ActivityDetailsPanelEmits {
  (event: 'close'): void;
  (eventName: 'ticketSelected', eventPayload: SelectTicketEventPayload): void;
}
const emit = defineEmits<ActivityDetailsPanelEmits>();

const activityDetailsStore = useActivityDetailsStore();
const activityDetailsMapStore = useActivityDetailsMapStore();
const activityDetailsTicketsStore = useActivityDetailsTicketsStore();
const mapStore = useMapStore();

// NOTE: Part of data required to create ActivityDetailsData interface is loaded in async manner,
// after activityDetails.vue is rendered to the screen. Hence we are updating inner state after component creation.
watch(
  () => props.activityDetails,
  () => {
    activityDetailsStore.setActivityDetails(props.activityDetails);
  },
  { immediate: true }
);

function scrollToTicket(): void {
  nextTick(() => {
    const element = document.getElementById(
      ActivityDetailsTemplateId.TicketRates
    );
    element?.scrollIntoView({ behavior: 'smooth' });
  });
}

function scrollToTicketWhenAvailable(): void {
  // NOTE: Hack fix to allow the DOM to update with latest status before attempting to scroll to tickets
  // TODO: Find a better solution to wait for the state to be final
  const BUFFER_TIME_TO_SCROLL = 250;

  if (!props.activityDetails.isAvailabilityLoading) {
    setTimeout(() => scrollToTicket(), BUFFER_TIME_TO_SCROLL);
  } else {
    const stop = watch(
      () => props.activityDetails.isAvailabilityLoading,
      (isLoadingAvailability) => {
        if (!isLoadingAvailability) {
          setTimeout(() => scrollToTicket(), BUFFER_TIME_TO_SCROLL);
          stop();
        }
      }
    );
  }
}

onMounted(() => {
  activityDetailsTicketsStore.setInitialSelectedDate();
  document.body.style.overflow = 'hidden';

  if (props.activityDetails.scrollToAvailability) {
    scrollToTicketWhenAvailable();
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
});

const isLoading = computed(
  () =>
    props.activityDetails.isAvailabilityLoading ||
    isFeatureToggleActive('loader')
);
</script>

<template>
  <AppFirstLevelPanel class="activity-details-panel" @close="emit('close')">
    <template #content>
      <div class="content-wrapper">
        <ActivityDetailsGalleryLoader v-if="isLoading" @close="emit('close')" />
        <ActivityDetailsGallery
          v-else-if="
            activityDetailsStore.headerData &&
            props.activityDetails.staticInfo.images
          "
          :gallery-type="activityDetailsStore.galleryType"
          :product-text="activityDetailsStore.headerData.headerText"
          :images="props.activityDetails.staticInfo.images"
          :activity-code="props.activityDetails.productCode"
          @close="emit('close')"
        />
        <ActivityDetailsDescription :is-loading="isLoading" />
        <ActivityDetailsTicketsLoader v-if="isLoading" />
        <ActivityDetailsTickets
          v-else
          :product-code="props.activityDetails.productCode"
          :activity-type="props.activityDetails.staticInfo.typeOfHotel"
          :is-alternative="!!props.activityDetails.isAlternative"
        />
      </div>
    </template>
    <template #footer>
      <ActivityDetailsFooterLoader v-if="isLoading" />
      <ActivityDetailsFooter
        v-else
        @close="emit('close')"
        @scroll-to-ticket="scrollToTicket"
      />
    </template>
  </AppFirstLevelPanel>

  <DynamicFullScreenMap
    v-if="activityDetailsMapStore.shouldShowFullscreenMap"
    :map-configuration="{
      markers: activityDetailsMapStore.mapMarker,
      markerFormatters: activityDetailsMapStore.getMapMarkerFormatters(
        MapType.Dynamic,
        mapStore.mapTypeId
      ),
      startRectangle: activityDetailsMapStore.initialMapView,
    }"
    @close="activityDetailsMapStore.closeDynamicMapModal"
  />
</template>

<style scoped lang="scss">
.activity-details-panel {
  background-color: var(--color-white);

  .content-wrapper {
    @include for-more-or-equal-than-1200-px {
      padding: var(--spacing-24) var(--spacing-24) var(--spacing-32)
        var(--spacing-24);
    }

    .divider {
      margin: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
      border-top: 1px solid var(--color-scrollbar);

      @include for-more-or-equal-than-1200-px {
        margin: 0 0 var(--spacing-32) 0;
      }
    }
  }
}
::-webkit-scrollbar-track {
  background: var(--color-white);
}
</style>
